import { h, Transition } from 'vue';
import { gsap, prefersReducedMotion } from '@/plugins/gsap';

// GSAP-driven <Transition> for Vuetify overlays (v-menu, backdrops).
// Use as: <v-menu :transition="{ component: GsapDropdownTransition, scale: 0.96, blur: 8 }">
// Vuetify passes its own hooks (onAfterEnter / onAfterLeave) and a `target`; hooks are
// forwarded, `target` is accepted as a prop so it never lands on the DOM.
//
// In: the panel glides down from the trigger, growing from `scale` and coming into focus
// from `blur` (px) on a long expo ease. Then [data-menu-stagger] children rise in one after
// another and any [data-menu-pop] inside them (icons) pops in with a soft overshoot.
// Out: the children sink away in reverse while the panel lifts, shrinks and softens.
// prefers-reduced-motion makes every duration 0.
export default {
  name: 'GsapDropdownTransition',
  inheritAttrs: false,
  props: {
    target: { type: null, default: undefined },
    y: { type: Number, default: -10 },
    duration: { type: Number, default: 0.3 },
    scale: { type: Number, default: 1 },
    blur: { type: Number, default: 0 },
  },
  setup(props, { slots, attrs }) {
    const time = (d) => (prefersReducedMotion() ? 0 : d);
    const from = () => ({
      autoAlpha: 0,
      y: props.y,
      scale: props.scale,
      filter: props.blur ? `blur(${props.blur}px)` : 'none',
    });

    return () =>
      h(
        Transition,
        {
          ...attrs,
          css: false,
          onEnter(el, done) {
            const items = el.querySelectorAll('[data-menu-stagger]');
            const pops = el.querySelectorAll('[data-menu-pop]');
            gsap.fromTo(el, from(), {
              autoAlpha: 1,
              y: 0,
              scale: 1,
              filter: props.blur ? 'blur(0px)' : 'none',
              transformOrigin: '50% 0%',
              duration: time(props.duration),
              ease: 'expo.out',
              overwrite: true,
              clearProps: 'transform,filter',
              onComplete: done,
            });
            if (items.length) {
              gsap.fromTo(
                items,
                { autoAlpha: 0, y: 14 },
                {
                  autoAlpha: 1,
                  y: 0,
                  duration: time(0.6),
                  delay: time(0.1),
                  stagger: time(0.06),
                  ease: 'expo.out',
                  overwrite: true,
                  clearProps: 'transform',
                }
              );
            }
            if (pops.length) {
              gsap.fromTo(
                pops,
                { scale: 0.5, rotate: -8 },
                {
                  scale: 1,
                  rotate: 0,
                  duration: time(0.55),
                  delay: time(0.18),
                  stagger: time(0.06),
                  ease: 'back.out(2.4)',
                  overwrite: true,
                  clearProps: 'transform',
                }
              );
            }
          },
          onLeave(el, done) {
            const items = el.querySelectorAll('[data-menu-stagger]');
            if (items.length) {
              gsap.to(items, {
                autoAlpha: 0,
                y: -8,
                duration: time(0.2),
                stagger: { each: time(0.03), from: 'end' },
                ease: 'power2.in',
                overwrite: true,
              });
            }
            gsap.to(el, {
              ...from(),
              duration: time(props.duration * 0.55),
              delay: time(items.length ? 0.1 : 0),
              ease: 'power3.in',
              overwrite: true,
              onComplete: done,
            });
          },
        },
        slots
      );
  },
};
